import {
  useState,
  useEffect,
  FC,
  useCallback,
  useMemo,
  HTMLAttributes,
  SyntheticEvent,
} from 'react';
import debounce from 'lodash/debounce';
import Autocomplete from '@mui/material/Autocomplete';
import { FormProps } from '../form/helpers';
import { UiOption } from '@/lib/helpers';
import FormControl from '../form/form-control';
import { Box, ListSubheader, Skeleton, TextField, Theme } from '@mui/material';
import usePrior from '@/hooks/use-prior.hook';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  subheader: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    lineHeight: '30px',
    marginTop: 10,
    paddingLeft: 8,
  },
  selectSearch: {
    '&.disabled': {
      '& .MuiInputBase-root': {
        backgroundColor: theme.palette.subtleGray,
      },
      '& fieldset': {
        borderColor: `${theme.palette.subtleGray} !important`,
      },
      '& .MuiAutocomplete-inputRoot': {
        color: theme.palette.lightGray,
      },
    },
    '& fieldset': {
      borderWidth: '1px !important',
    },
  },
}));

const filterOptionsByKeyword = (allOptions: UiOption[], keyword: string): UiOption[] => {
  const safeLower = keyword.trim().toLowerCase();
  if (!safeLower) {
    return allOptions;
  }

  const matchingValues = new Set(
    allOptions
      .filter((option) => !option.meta?.header)
      .filter(({ label, labelSimple }) =>
        String(labelSimple || label || '')
          .toLowerCase()
          .includes(safeLower)
      )
      .map(({ value }) => value)
  );

  const filtered: UiOption[] = [];
  let pendingHeader: UiOption | null = null;

  allOptions.forEach((option) => {
    if (option.meta?.header) {
      pendingHeader = option;
      return;
    }

    if (matchingValues.has(option.value)) {
      if (pendingHeader) {
        filtered.push(pendingHeader);
        pendingHeader = null;
      }
      filtered.push(option);
    }
  });

  return filtered;
};

interface Props extends FormProps {
  value: string | undefined;
  onChange: (value: string | undefined, option: UiOption | null, event: SyntheticEvent) => void;
  onSearch?: (value: string) => Promise<UiOption[]>;
  options: UiOption[];
  loading?: boolean;
}

const SelectSearch: FC<Props> = ({
  onSearch,
  onChange,
  label,
  error = '',
  name,
  value,
  size = 'medium',
  disabled = false,
  readonly = false,
  required = false,
  fullWidth = true,
  options: propOptions = [],
  loading = false,
}) => {
  const styles = useStyles();
  const labelId = `${name}-label`;
  const [options, setOptions] = useState<UiOption[]>(propOptions);
  const [keyword, setKeyword] = useState('');

  const selectedOption = useMemo<UiOption | null>(() => {
    if (!value) {
      return null;
    }
    return propOptions.find((option) => option.value === value) ?? null;
  }, [propOptions, value]);

  const handleChange = (event: SyntheticEvent, selected: UiOption | null) => {
    if (selected?.meta?.header) {
      return;
    }
    onChange(selected?.value, selected, event);
  };

  const handleInputChange = (_event: SyntheticEvent, inputValue: string, reason: string) => {
    if (reason === 'clear') {
      setKeyword('');
      return;
    }

    if (reason === 'input') {
      setKeyword(inputValue);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceOnSearch = useCallback(
    debounce((keyword: string) => {
      const safeKeyword = keyword.trim();

      if (onSearch) {
        onSearch(safeKeyword).then(setOptions);
        return;
      }

      setOptions(filterOptionsByKeyword(propOptions, safeKeyword));
    }, 600),
    [onSearch, propOptions]
  );

  const handleRenderOption = (props: HTMLAttributes<HTMLLIElement>, option: UiOption) => {
    const { key: listKey, ...restProps } = props as HTMLAttributes<HTMLLIElement> & {
      key?: string;
    };
    const optionKey = option.key || option.value;

    if (option.meta?.header) {
      return (
        <ListSubheader className={styles.subheader} key={listKey ?? optionKey}>
          {option.label}
        </ListSubheader>
      );
    }

    return (
      <Box component="li" {...restProps} key={listKey ?? optionKey}>
        {option.label}
      </Box>
    );
  };

  const handleGetOptionDisabled = (option: UiOption) => !!option.meta?.header;

  const handleOptionEqual = (option: UiOption, selected: UiOption) =>
    option?.value === selected?.value;

  useEffect(() => {
    if (!keyword.trim()) {
      setOptions(propOptions);
      return;
    }

    debounceOnSearch(keyword);
  }, [keyword, debounceOnSearch, propOptions]);

  // if some other action clears the value (like resetting a form), clear the keyword
  const priorValue = usePrior(value);
  useEffect(() => {
    if (!value && priorValue) {
      setKeyword('');
    }
  }, [value, priorValue]);

  const componentSize = size === 'x-small' ? 'small' : size;
  const compClass = classNames(styles.selectSearch, { disabled: disabled || readonly });

  return (
    <FormControl error={error} name={name} size={size} fullWidth={fullWidth} className={compClass}>
      <Autocomplete
        options={options}
        filterOptions={(searchResultOptions) => searchResultOptions}
        getOptionLabel={({ label, labelSimple }) => labelSimple || (label as string) || ''}
        onChange={handleChange}
        onInputChange={handleInputChange}
        value={selectedOption}
        size={componentSize}
        fullWidth={fullWidth}
        disabled={disabled || readonly}
        noOptionsText="&nbsp;"
        loading={loading}
        renderOption={handleRenderOption}
        getOptionDisabled={handleGetOptionDisabled}
        isOptionEqualToValue={handleOptionEqual}
        ListboxProps={{
          style: { maxHeight: 300, overflow: 'auto' },
        }}
        renderInput={(params) => {
          const TextFieldEl = (
            <TextField
              {...params}
              id={name}
              name={name}
              label={label as string}
              variant="outlined"
              error={!!error}
              required={required}
              InputLabelProps={{
                ...params.InputLabelProps,
                id: labelId,
              }}
            />
          );

          if (loading) {
            return (
              <Skeleton variant="rounded" width="100%">
                {TextFieldEl}
              </Skeleton>
            );
          }

          return TextFieldEl;
        }}
      />
    </FormControl>
  );
};

export default SelectSearch;
